import htmlEncode from 'js-htmlencode'
import { envelope } from './index'

/**
 * @return {string}
 */
export function UpdateItemWithBody(id, changeKey, newBody, newSubject, recipients) {
  return SendAndSaveCopy(id, changeKey, newBody, newSubject, recipients)
}

/**
 * @return {string}
 */
export function UpdateItem(id, changeKey) {
  return SaveOnly(id, changeKey)
}

/**
 * Updates message that has a reply.
 * It updates these MAPI properties responsible for the "reply infobar":
 *  1. 0x1080 - a number that indicates which icon to use
 *              when you display a group of e-mail objects.
 *  2. 0x1081 - the last verb executed.
 *  3. 0x1082 - the time when the last verb was executed.
 *
 * Microsoft states that the time must be in FILETIME format, however
 * their EWS API doesn't accept it.
 * Empirically found that ISO string is required.
 *
 * @see {@link https://msdn.microsoft.com/en-us/library/office/cc815472.aspx | 0x1080}
 * @see {@link https://msdn.microsoft.com/en-us/library/office/cc841968.aspx | 0x1081}
 * @see {@link https://msdn.microsoft.com/en-us/library/office/cc839918.aspx | 0x1082}
 *
 * @param {string} id
 * @param {string} changeKey
 * @return {Promise.<Document, Error>} response
 */
function SaveOnly(id, changeKey) {
  return envelope`
    <m:UpdateItem MessageDisposition="SaveOnly" ConflictResolution="AlwaysOverwrite">
      <m:SavedItemFolderId>
        <t:DistinguishedFolderId Id="sentitems" />
      </m:SavedItemFolderId>
      <m:ItemChanges>
        <t:ItemChange>
          <t:ItemId Id="${id}" ChangeKey="${changeKey}"/>
          <t:Updates>
            <t:SetItemField>
              <t:ExtendedFieldURI PropertyTag="0x1080" PropertyType="Integer" />
              <t:Message>
                <t:ExtendedProperty>
                  <t:ExtendedFieldURI PropertyTag="0x1080" PropertyType="Integer" />
                  <t:Value>261</t:Value>
                </t:ExtendedProperty>
              </t:Message>
            </t:SetItemField>
            <t:SetItemField>w
              <t:ExtendedFieldURI PropertyTag="0x1081" PropertyType="Integer" />
              <t:Message>
                <t:ExtendedProperty>
                  <t:ExtendedFieldURI PropertyTag="0x1081" PropertyType="Integer" />
                  <t:Value>103</t:Value>
                </t:ExtendedProperty>
              </t:Message>
            </t:SetItemField>
            <t:SetItemField>
              <t:ExtendedFieldURI PropertyTag="0x1082" PropertyType="SystemTime" />
              <t:Message>
                <t:ExtendedProperty>
                  <t:ExtendedFieldURI PropertyTag="0x1082" PropertyType="SystemTime" />
                  <t:Value>${new Date().toISOString()}</t:Value>
                </t:ExtendedProperty>
              </t:Message>
            </t:SetItemField>
          </t:Updates>
        </t:ItemChange>
      </m:ItemChanges>
    </m:UpdateItem>
  `
}

function SendAndSaveCopy(id, changeKey, newBody, newSubject, recipients) {
  return envelope`
    <m:UpdateItem MessageDisposition="SendAndSaveCopy" ConflictResolution="NeverOverwrite">
        <m:SavedItemFolderId>
         <t:DistinguishedFolderId Id="sentitems" />
        </m:SavedItemFolderId>
        <m:ItemChanges>
          <t:ItemChange>
            <t:ItemId Id="${id}" ChangeKey="${changeKey}"/>
            <t:Updates>
              ${newSubject ? UpdateSubjectTemplate(newSubject) : ''}
              <t:SetItemField>
                <t:FieldURI FieldURI="item:Body"/>
                <t:Message>
                  <t:Body BodyType="HTML">${htmlEncode(newBody)}</t:Body>
                </t:Message>
              </t:SetItemField>
              ${UpdateToRecipients(recipients.to)}
              ${UpdateCcRecipients(recipients.cc)}
              ${UpdateBccRecipients(recipients.bcc)}
            </t:Updates>
          </t:ItemChange>
        </m:ItemChanges>
      </m:UpdateItem>
  `
}

function GetMailboxTemplate(recipients) {
  return recipients.map(
    ({ emailAddress, displayName }) => `
      <t:Mailbox>
        <t:EmailAddress>${emailAddress}</t:EmailAddress>
        <t:Name>${htmlEncode(displayName) || ''}</t:Name>
      </t:Mailbox>
    `
  )
}

function UpdateToRecipients(recipients) {
  return `
    <t:SetItemField>
      <t:FieldURI FieldURI="message:ToRecipients"/>
      <t:Message>
        <t:ToRecipients>
          ${GetMailboxTemplate(recipients)}
        </t:ToRecipients>
      </t:Message>
    </t:SetItemField>
  `
}

function UpdateCcRecipients(recipients) {
  if (recipients.length === 0) {
    return ''
  }

  return `
    <t:SetItemField>
      <t:FieldURI FieldURI="message:CcRecipients"/>
      <t:Message>
        <t:CcRecipients>
          ${GetMailboxTemplate(recipients)}
        </t:CcRecipients>
      </t:Message>
    </t:SetItemField>
  `
}

function UpdateBccRecipients(recipients) {
  if (recipients.length === 0) {
    return ''
  }

  return `
    <t:SetItemField>
      <t:FieldURI FieldURI="message:BccRecipients"/>
      <t:Message>
        <t:BccRecipients>
          ${GetMailboxTemplate(recipients)}
        </t:BccRecipients>
      </t:Message>
    </t:SetItemField>
  `
}

function UpdateSubjectTemplate(newSubject) {
  return `<t:SetItemField>
            <t:FieldURI FieldURI="item:Subject"/>
            <t:Message>
              <t:Subject>${htmlEncode(newSubject)}</t:Subject>
            </t:Message>
          </t:SetItemField>`
}
